import { Injectable, computed, signal } from '@angular/core';

import { AuthFacade } from '@cpd-app/state/auth/auth.facade';
import {
  Observable,
  catchError,
  filter,
  finalize,
  map,
  of,
  switchMap,
  take,
  tap,
  timeout
} from 'rxjs';

import {
  MyPortfolioGQL,
  PublishPortfolioGQL,
  SavePortfolioGQL,
  UnpublishPortfolioGQL
} from '@cpd-app/generated';

import {
  PortfolioDocument,
  emptyPortfolio,
  publishBlockedReason
} from './portfolio.model';

/**
 * The four states the publish toolbar can be in. 06-transition section 5.
 *
 * `pending` is live with unpublished edits, which is the state a single
 * boolean cannot express and the one a member most needs to see.
 */
export type PublishState = 'draft' | 'live' | 'pending' | 'down';

/**
 * What a write actually did, so the caller can say so rather than assume it.
 *
 * The builder used to announce "Saved." and "Your page is live." the moment the
 * button was pressed, which meant it announced them for writes the server
 * refused and for writes that never left the browser.
 *
 *  - `done`: the server answered with the document.
 *  - `failed`: the write was attempted and did not land.
 *  - `refused`: this session would not attempt it. Today that is a read that
 *    failed, where writing would replace a real portfolio with an empty one.
 */
export type PortfolioWriteOutcome = 'done' | 'failed' | 'refused';

/** The mutation response shape, which is the same for all three writes. */
interface PortfolioResponse {
  slug: string;
  draft: unknown;
  publishedAt?: unknown;
  isLive: boolean;
  unpublishedSections: string[];
}

/**
 * PWS 4.1.19. The portfolio builder's state.
 *
 * The draft lives here as a signal and is saved explicitly. Autosaving a
 * document a member is midway through editing would make "publish" and "save"
 * indistinguishable, and publishing is the only thing that should change what a
 * visitor sees.
 */
/** How long the typing has to stop before the draft is written. */
const SAVE_DELAY_MS = 1200;

@Injectable({ providedIn: 'root' })
export class PortfolioService {
  readonly #draft = signal<PortfolioDocument>(emptyPortfolio());
  readonly #slug = signal<string>('');
  readonly #isLive = signal(false);
  readonly #publishedAt = signal<string | null>(null);
  readonly #unpublishedSections = signal<string[]>([]);
  readonly #hasLoaded = signal(false);

  /**
   * The read failed, as distinct from the member having no portfolio yet.
   *
   * These look identical in the builder, and confusing them loses data: an
   * empty draft rendered over a failed read is one Save away from overwriting
   * a real portfolio with nothing. Every write below refuses while this is set.
   */
  readonly #loadFailed = signal(false);
  readonly #isSaving = signal(false);

  public readonly draft = this.#draft.asReadonly();
  public readonly slug = this.#slug.asReadonly();
  public readonly isLive = this.#isLive.asReadonly();
  public readonly publishedAt = this.#publishedAt.asReadonly();
  public readonly unpublishedSections = this.#unpublishedSections.asReadonly();

  /**
   * Mark a section as changed since the page was last published.
   *
   * `unpublishedSections` arrives from the SERVER, computed at load time by
   * comparing the stored draft with the stored published copy. Anything that
   * changes the draft AFTER that read leaves the value stale, and the toolbar
   * goes on saying "Live" over a page that no longer matches.
   *
   * That is what hid the resume copy. Experience is copied in from the resume
   * once both reads land, which can easily be after the member has published:
   * measured on dev, one member published at 14:14:48 and the copy wrote the
   * draft at 14:15:48, so their public page served an empty Experience section
   * while the builder said everything was live.
   */
  public markUnpublished(key: string): void {
    this.#unpublishedSections.update((held) =>
      held.includes(key) ? held : [...held, key]
    );
  }
  public readonly hasLoaded = this.#hasLoaded.asReadonly();
  public readonly loadFailed = this.#loadFailed.asReadonly();
  public readonly isSaving = this.#isSaving.asReadonly();

  /** Null when publishing is allowed, otherwise the reason it is not. */
  public readonly publishBlocked = computed(() =>
    publishBlockedReason(this.#draft())
  );

  public readonly publishState = computed<PublishState>(() => {
    if (!this.#publishedAt()) {
      return 'draft';
    }

    if (!this.#isLive()) {
      return 'down';
    }

    return this.#unpublishedSections().length ? 'pending' : 'live';
  });

  /**
   * The member's public page, as an app path.
   *
   * Jackson's item 23. This used to be built inside the portfolio builder, so
   * the route `/p/<slug>` was written down in a component and the resume
   * builder, which needs the same link under the member's name, had nowhere to
   * read it from. The route is one fact: it is stated once here and read twice.
   */
  public readonly publicUrl = computed(() =>
    this.#slug() ? `/p/${this.#slug()}` : ''
  );

  /**
   * The same link, absolute.
   *
   * What a member pastes into an email, or an employer reads off a printed
   * resume, has to work from outside the app: `/p/<slug>` on paper is not a
   * link at all.
   */
  public readonly publicUrlAbsolute = computed(() => {
    const path = this.publicUrl();

    return path ? new URL(path, window.location.origin).toString() : '';
  });

  /**
   * The page has been published at some point, so the member has a link.
   *
   * Deliberately true while the state is `down`: the link is still theirs, and
   * the builder goes on showing it so they have something to put back. It is
   * NOT the test for whether a visitor can open it, which is `isLinkOpenable`.
   */
  public readonly isPublished = computed(
    () => this.publishState() !== 'draft' && !!this.#slug()
  );

  /**
   * A visitor following this link right now reaches a page.
   *
   * `down` is the whole reason these are two computeds and not one: the member
   * has a link and it resolves to nothing. Anywhere the link leaves the app,
   * and a printed resume above all, has to gate on this one. A dead
   * `/p/<slug>` under somebody's name is worse than no link.
   */
  public readonly isLinkOpenable = computed(
    () =>
      !!this.#slug() &&
      (this.publishState() === 'live' || this.publishState() === 'pending')
  );

  public readonly publishMessage = computed(() => {
    switch (this.publishState()) {
      case 'live':
        return 'Live. Last published today';
      case 'pending':
        return 'Live, with changes a visitor cannot see yet';
      case 'down':
        return 'Not visible. Your link is inactive';
      default:
        return 'Only you can see this';
    }
  });

  /**
   * What the state means for the LINK, which is the thing a member is actually
   * asking about. The headline names the state; this says who can open the page
   * right now, and each one is the design's own sentence.
   */
  public readonly publishDetail = computed(() => {
    switch (this.publishState()) {
      case 'live': {
        const count = this.#draft()?.order?.length ?? 0;
        return `Anyone with the link can read this page. ${count} ${
          count === 1 ? 'section' : 'sections'
        } on it.`;
      }
      case 'pending':
        return 'The link shows the published version, not your edits.';
      case 'down':
        return 'Nobody can open your link until you put it back.';
      default:
        return 'Nobody can open this link until you publish it.';
    }
  });

  constructor(
    private authFacade: AuthFacade,
    private myPortfolioGQL: MyPortfolioGQL,
    private savePortfolioGQL: SavePortfolioGQL,
    private publishPortfolioGQL: PublishPortfolioGQL,
    private unpublishPortfolioGQL: UnpublishPortfolioGQL
  ) {}

  /**
   * T.7. Whether a read is out. See the guard at the top of `load`.
   */
  #reading = false;

  public load(): void {
    /*
     * T.7. ONE READ AT A TIME.
     *
     * The resume tab starts a read on init and the portfolio tab starts
     * another, so opening one then the other put two reads in flight. Each
     * one REPLACES the draft when it lands, so the second deleted anything
     * written in between: measured in Storybook, the page name the builder
     * had just filled in from the resume was set, then wiped by the second
     * read seconds later. A read already out answers both callers.
     */
    if (this.#reading) {
      return;
    }
    this.#reading = true;

    /*
     * WAIT FOR THE TOKEN. `myPortfolio` is permission-gated and this used to
     * fire straight out of component init, racing the session: the request
     * went out with no Authorization header and came back "Forbidden
     * resource" as a normal HTTP 200, which this method correctly reads as a
     * failed read. The member was then told their page could not be loaded and
     * that it was not safe to edit, and reloading was the only way out.
     *
     * The token and not `isSessionInitializing$`: that flag clears BEFORE the
     * token reaches the store, and the interceptor attaches the header only
     * once the token is there.
     */
    this.authFacade.authToken$
      .pipe(
        filter((token) => !!token),
        take(1),
        // A visitor who is not signed in still gets one attempt rather than a
        // builder that never finishes loading.
        timeout({ first: 3000, with: () => of('') }),
        switchMap(() =>
          this.myPortfolioGQL.fetch({}, { fetchPolicy: 'network-only' })
        )
      )
      .pipe(
        map((result) => result?.data?.myPortfolio ?? null),
        /*
         * ONE DOCUMENT, THEN DONE. `catchError` sits downstream of the
         * emission, so a late error on this query arrived AFTER a good
         * document had already flowed through `tap` and latched the failed
         * flag with nothing to reset it. The member sat looking at their own
         * correctly rendered portfolio while `save`, `publish` and `unpublish`
         * were all silently refused.
         *
         * An error arriving with NO document still reaches `catchError`, which
         * is the case that has to keep working.
         */
        take(1),
        // A read that FAILED is not a member with no portfolio yet. Both would
        // render an empty builder, and saving from that state would overwrite
        // a real portfolio with an empty one, so they are kept apart.
        catchError(() => {
          this.#loadFailed.set(true);
          return of(null);
        }),
        tap((portfolio) => {
          if (portfolio) {
            this.apply(portfolio as PortfolioResponse, 'read');
          }

          this.#hasLoaded.set(true);
        }),
        finalize(() => (this.#reading = false))
      )
      .subscribe();
  }

  /**
   * Changes the draft, and writes it once the typing stops. The design set's
   * control bar carries no Save: editing changes the draft, and Publish is the
   * only thing that publishes. A write per keystroke is not saving, it is a
   * load test, so it is held back until there is a pause.
   */
  public update(changes: Partial<PortfolioDocument>): void {
    this.#draft.update((current) => ({ ...current, ...changes }));
    this.queueSave();
  }

  private saveTimer: ReturnType<typeof setTimeout> | null = null;

  private queueSave(): void {
    this.cancelQueued();
    this.saveTimer = setTimeout(() => {
      this.saveTimer = null;
      this.writeDraft().subscribe();
    }, SAVE_DELAY_MS);
  }

  private cancelQueued(): void {
    if (this.saveTimer !== null) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }
  }

  /**
   * Sends a pending write now, so nothing is lost on the way out.
   *
   * Cold: the caller subscribes. Nothing pending is `done`, because there is
   * nothing to lose.
   */
  public flush(): Observable<PortfolioWriteOutcome> {
    return this.saveTimer === null ? of('done' as const) : this.save();
  }

  /**
   * Writes the draft now, cancelling anything queued.
   *
   * Cancelling matters as much as writing: a timer left running fires after
   * the write it duplicates and re-sends whatever the draft held when it was
   * set, which is how publishing used to persist the text it had just reverted.
   */
  public save(): Observable<PortfolioWriteOutcome> {
    this.cancelQueued();

    return this.writeDraft();
  }

  private writeDraft(): Observable<PortfolioWriteOutcome> {
    // Never write over a portfolio this session could not read.
    if (this.#loadFailed()) {
      return of('refused');
    }

    this.#isSaving.set(true);

    return this.savePortfolioGQL.mutate({ draft: this.#draft() }).pipe(
      map((result) => result?.data?.savePortfolio ?? null),
      catchError(() => of(null)),
      map((portfolio) => {
        if (portfolio) {
          this.apply(portfolio as PortfolioResponse, 'echo');
        }

        this.#isSaving.set(false);

        return portfolio ? ('done' as const) : ('failed' as const);
      })
    );
  }

  /**
   * Publishes what the member has written, which means writing it first.
   *
   * Publishing is a server-side copy of the stored draft over the published
   * document, so a pending keystroke has to LAND before the copy or the page
   * goes live carrying the previous draft. Waiting for the write is the whole
   * of the fix; publishing anyway would publish something the member cannot
   * see and did not choose.
   */
  public publish(): Observable<PortfolioWriteOutcome> {
    if (this.#loadFailed() || this.publishBlocked()) {
      return of('refused');
    }

    return this.save().pipe(
      switchMap((written) =>
        written === 'done'
          ? this.runWrite(this.publishPortfolioGQL.mutate(), 'publishPortfolio')
          : of(written)
      )
    );
  }

  /**
   * Takes the page down.
   *
   * A pending write goes out first so the stale timer cannot fire behind the
   * response, but it does not gate the unpublish: taking a page down is the
   * urgent half of this pair, and a member asking for it should not be held up
   * by a draft that will not save.
   */
  public unpublish(): Observable<PortfolioWriteOutcome> {
    if (this.#loadFailed()) {
      return of('refused');
    }

    return this.flush().pipe(
      switchMap(() =>
        this.runWrite(
          this.unpublishPortfolioGQL.mutate(),
          'unpublishPortfolio'
        )
      )
    );
  }

  /** The shared tail of publish and unpublish: mutate, apply, report. */
  private runWrite(
    mutation: Observable<{ data?: unknown } | null>,
    field: 'publishPortfolio' | 'unpublishPortfolio'
  ): Observable<PortfolioWriteOutcome> {
    return mutation.pipe(
      map((result) => {
        const data = (result?.data ?? null) as Record<
          string,
          PortfolioResponse | null
        > | null;

        return data?.[field] ?? null;
      }),
      catchError(() => of(null)),
      map((portfolio) => {
        if (portfolio) {
          this.apply(portfolio, 'echo');
        }

        return portfolio ? ('done' as const) : ('failed' as const);
      })
    );
  }

  /**
   * Takes the server's answer.
   *
   * The slug, the live flag, the publish date and which sections are behind
   * are the server's to state, and they are taken from every answer.
   *
   * The draft is not. A mutation's copy of it is the document the SERVER holds,
   * which is already a round trip behind the moment the member typed anything
   * while the request was out, so setting it deletes those keystrokes: that is
   * the data loss publishing used to cause, and there is nothing in a mutation
   * echo the local draft does not already have. Only the READ supplies a draft,
   * because only the read is where the local draft comes from.
   */
  private apply(portfolio: PortfolioResponse, from: 'read' | 'echo'): void {
    this.#slug.set(portfolio.slug);
    this.#isLive.set(portfolio.isLive);
    this.#publishedAt.set(
      portfolio.publishedAt ? String(portfolio.publishedAt) : null
    );
    this.#unpublishedSections.set(portfolio.unpublishedSections ?? []);

    if (from === 'echo') {
      return;
    }

    // Merged over an empty document rather than trusted wholesale: a stored
    // draft written before a section existed would otherwise arrive without it.
    this.#draft.set({
      ...emptyPortfolio(),
      ...((portfolio.draft as PortfolioDocument) ?? {})
    });
  }
}
